import * as opentype from 'opentype.js';
import { FontItem } from '../types';
import { saveFont, loadFontBlob, deleteFont } from './fontStorage';

class FontService {
  private customFonts: FontItem[] = [];
  private loadedBuiltInFonts: FontItem[] = [];

  /**
   * Load a built-in font by downloading it from a URL.
   * Caches the result so subsequent calls are instant.
   */
  async loadBuiltInFont(font: FontItem): Promise<void> {
    // Already loaded — skip
    if (this.loadedBuiltInFonts.find((f) => f.id === font.id)) return;

    const url = font.url;
    if (!url) throw new Error(`Built-in font "${font.name}" has no URL`);

    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to download font: ${response.status}`);
    const blob = await response.blob();
    const arrayBuffer = await blob.arrayBuffer();

    const fontFace = new FontFace(font.family, arrayBuffer);
    await fontFace.load();
    document.fonts.add(fontFace);

    // Store in IndexedDB so it persists across reloads
    await saveFont(font, blob);

    this.loadedBuiltInFonts.push(font);
  }

  async loadFont(file: File): Promise<FontItem> {
    const fontFamily = `custom-font-${Date.now()}`;
    const arrayBuffer = await file.arrayBuffer();

    const fontName = this.extractFontName(arrayBuffer, file.name);

    const fontFace = new FontFace(fontFamily, arrayBuffer);
    await fontFace.load();
    document.fonts.add(fontFace);

    const newFont: FontItem = {
      id: fontFamily,
      name: fontName,
      desc: file.name,
      family: fontFamily,
      isCustom: true,
    };

    // Store the binary font data in IndexedDB
    await saveFont(newFont, file);

    this.customFonts.push(newFont);
    return newFont;
  }

  async reloadFont(font: FontItem): Promise<boolean> {
    try {
      const blob = await loadFontBlob(font.id);
      if (!blob) return false;

      const fontFace = new FontFace(font.family, await blob.arrayBuffer());
      await fontFace.load();
      document.fonts.add(fontFace);
      return true;
    } catch {
      console.warn(`Failed to reload font: ${font.name}`);
      return false;
    }
  }

  async removeFont(fontId: string): Promise<void> {
    await deleteFont(fontId);
    this.customFonts = this.customFonts.filter((f) => f.id !== fontId);
  }

  private extractFontName(arrayBuffer: ArrayBuffer, fileName: string): string {
    try {
      const font = opentype.parse(arrayBuffer);
      const names = font.names as unknown as Record<
        string,
        Record<string, Record<string, string>>
      >;

      const platforms = ['windows', 'macintosh'] as const;
      // Prefer fontFamily/fullName (human-readable) over postScriptName (internal ID)
      const nameTypes = ['fontFamily', 'fullName', 'postScriptName'] as const;
      const languages = ['zh', 'zh-CN', 'zh-TW', 'zh-HK', 'en'] as const;

      for (const platform of platforms) {
        const platformNames = names[platform];
        if (!platformNames || typeof platformNames !== 'object') continue;

        for (const nameType of nameTypes) {
          const nameEntry = platformNames[nameType];
          if (!nameEntry || typeof nameEntry !== 'object') continue;

          for (const lang of languages) {
            const value = nameEntry[lang];
            if (value && typeof value === 'string' && value.trim()) {
              return value.trim();
            }
          }
        }
      }
    } catch {
      // opentype.js may fail on some font formats
    }

    return fileName.replace(/\.(ttf|otf|woff|woff2)$/i, '');
  }

  isFontLoaded(fontId: string): boolean {
    return this.loadedBuiltInFonts.some((f) => f.id === fontId);
  }

  /** Alias for loadBuiltInFont — loads a font from its URL */
  async loadFontFromUrl(font: FontItem): Promise<void> {
    return this.loadBuiltInFont(font);
  }

  getCustomFonts(): FontItem[] {
    return this.customFonts;
  }
}

export const fontService = new FontService();
